// src/utils/dashboardDownloadUtils.js

/* ---------------- CSV HELPERS ---------------- */

const csvEscape = (val) => {
	if (val === null || val === undefined) return "";
	const s = String(val);
	if (/[,"\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
	return s;
};

const toCsv = (rows = [], columns = []) => {
	const header = columns.map((c) => csvEscape(c.label)).join(",");
	const body = (rows || [])
		.map((r) => columns.map((c) => csvEscape(c.get(r))).join(","))
		.join("\n");
	return [header, body].filter(Boolean).join("\n");
};

const downloadTextFile = (filename, text, mime = "text/csv;charset=utf-8;") => {
	const blob = new Blob([text], { type: mime });
	const url = URL.createObjectURL(blob);

	const a = document.createElement("a");
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();

	setTimeout(() => URL.revokeObjectURL(url), 500);
};

const num = (v) => {
	const n = Number(v);
	return Number.isFinite(n) ? n : 0;
};

const keyOf = (date, domain) =>
	`${String(date || "").trim()}__${String(domain || "").trim()}`;

/* ---------------- REVENUE CSV ---------------- */

/**
 * Revenue CSV row format (one row per Date+Domain):
 * partnerName,date,domain,StoryIQ_Revenue,ImageIQ_Revenue,PulseIQ_Revenue,VideoIQ_Revenue,Total_Revenue,Inventory
 *
 * Revenue from revenueDomainRows
 * Inventory from analyticsDomainRows.InventoryCreated
 */
export const buildRevenueCsvRows = ({
	partnerName,
	revenueDomainRows = [],
	analyticsDomainRows = []
}) => {
	const revMap = new Map();
	for (const r of revenueDomainRows || []) {
		const date = r?.Date;
		const domain = r?.Domain;
		if (!date || !domain) continue;
		revMap.set(keyOf(date, domain), r);
	}

	const anaMap = new Map();
	for (const a of analyticsDomainRows || []) {
		const date = a?.Date;
		const domain = a?.Domain;
		if (!date || !domain) continue;
		anaMap.set(keyOf(date, domain), a);
	}

	const keys = new Set([...revMap.keys(), ...anaMap.keys()]);
	const out = [];

	for (const k of keys) {
		const rev = revMap.get(k) || {};
		const ana = anaMap.get(k) || {};

		const date = rev.Date || ana.Date || "";
		const domain = rev.Domain || ana.Domain || "";

		out.push({
			partnerName: partnerName || "",
			date,
			domain,
			StoryIQ_Revenue: num(rev.StoryIQ_Revenue),
			ImageIQ_Revenue: num(rev.ImageIQ_Revenue),
			PulseIQ_Revenue: num(rev.PulseIQ_Revenue),
			VideoIQ_Revenue: num(rev.VideoIQ_Revenue),
			Total_Revenue: num(rev.Total_Revenue),
			Inventory: num(ana.InventoryCreated)
		});
	}

	out.sort((a, b) => {
		const d = String(a.date).localeCompare(String(b.date));
		if (d !== 0) return d;
		return String(a.domain).localeCompare(String(b.domain));
	});

	return out;
};

export const downloadRevenueDomainCSV = ({
	partnerName,
	filters, // {startDate, endDate}
	revenueDomainRows = [],
	analyticsDomainRows = [],
	decimalPlaces = 4
}) => {
	const start = filters?.startDate || "NA";
	const end = filters?.endDate || "NA";

	const rows = buildRevenueCsvRows({
		partnerName,
		revenueDomainRows,
		analyticsDomainRows
	});

	const fixed = (n) => Number(n || 0).toFixed(decimalPlaces);

	const columns = [
		{ label: "partnerName", get: (r) => r.partnerName },
		{ label: "date", get: (r) => r.date },
		{ label: "domain", get: (r) => r.domain },
		{ label: "StoryIQ_Revenue", get: (r) => fixed(r.StoryIQ_Revenue) },
		{ label: "ImageIQ_Revenue", get: (r) => fixed(r.ImageIQ_Revenue) },
		{ label: "PulseIQ_Revenue", get: (r) => fixed(r.PulseIQ_Revenue) },
		{ label: "VideoIQ_Revenue", get: (r) => fixed(r.VideoIQ_Revenue) },
		{ label: "Total_Revenue", get: (r) => fixed(r.Total_Revenue) },
		{ label: "Inventory", get: (r) => Math.round(r.Inventory) }
	];

	const csv = toCsv(rows, columns);
	downloadTextFile(`${start}_to_${end}_revenue_domain_data.csv`, csv);
};

/* ---------------- ANALYTICS CSV ---------------- */

/**
 * Analytics CSV row format (one row per Date+Domain):
 * Partner Name, Domain, Date,
 * <solution-specific engagement times>,
 * Total_EngagementTime, EngagementBoost, CTR
 *
 * CTR = (sum clicks / inventory) * 100
 * - clicks = IQClicks (already total)
 * - inventory = InventoryCreated
 *
 * EngagementBoost (%) = ((Total_EngagementTime - Article_EngagementTime) / Article_EngagementTime) * 100
 * If Article_EngagementTime == 0 => 0
 */
export const buildAnalyticsCsvRows = ({
	partnerName,
	analyticsDomainRows = []
}) => {
	const out = [];

	for (const a of analyticsDomainRows || []) {
		const date = a?.Date || "";
		const domain = a?.Domain || "";

		const story = num(a?.StoryIQ_EngagementTime);
		const image = num(a?.ImageIQ_EngagementTime);
		const pulse = num(a?.PulseIQ_EngagementTime);
		const video = num(a?.VideoIQ_EngagementTime);

		const totalEng = num(a?.Total_EngagementTime);
		const articleEng = num(a?.Article_EngagementTime);

		const inventory = num(a?.InventoryCreated);
		const clicksTotal = num(a?.IQClicks); // already “sum of clicks from every solution” per your schema

		const ctr = inventory > 0 ? (clicksTotal / inventory) * 100 : 0;

		const boost = articleEng > 0 ? (totalEng / articleEng) * 100 : 0;

		out.push({
			partnerName: partnerName || "",
			domain,
			date,

			StoryIQ_EngagementTime: story,
			ImageIQ_EngagementTime: image,
			PulseIQ_EngagementTime: pulse,
			VideoIQ_EngagementTime: video,

			Total_EngagementTime: totalEng,
			EngagementBoost: boost,
			CTR: ctr
		});
	}

	out.sort((a, b) => {
		const d = String(a.date).localeCompare(String(b.date));
		if (d !== 0) return d;
		return String(a.domain).localeCompare(String(b.domain));
	});

	return out;
};

export const downloadAnalyticsDomainCSV = ({
	partnerName,
	filters, // {startDate, endDate}
	analyticsDomainRows = [],
	decimalPlaces = 2
}) => {
	const start = filters?.startDate || "NA";
	const end = filters?.endDate || "NA";

	const rows = buildAnalyticsCsvRows({ partnerName, analyticsDomainRows });

	const fixed = (n) => Number(n || 0).toFixed(decimalPlaces);

	const columns = [
		{ label: "Partner Name", get: (r) => r.partnerName },
		{ label: "Domain", get: (r) => r.domain },
		{ label: "Date", get: (r) => r.date },

		{
			label: "StoryIQ_EngagementTime",
			get: (r) => fixed(r.StoryIQ_EngagementTime)
		},
		{
			label: "ImageIQ_EngagementTime",
			get: (r) => fixed(r.ImageIQ_EngagementTime)
		},
		{
			label: "PulseIQ_EngagementTime",
			get: (r) => fixed(r.PulseIQ_EngagementTime)
		},
		{
			label: "VideoIQ_EngagementTime",
			get: (r) => fixed(r.VideoIQ_EngagementTime)
		},

		{
			label: "Total_EngagementTime",
			get: (r) => fixed(r.Total_EngagementTime)
		},
		{ label: "Engagement Boost (%)", get: (r) => fixed(r.EngagementBoost) },
		{ label: "CTR (%)", get: (r) => fixed(r.CTR) }
	];

	const csv = toCsv(rows, columns);
	downloadTextFile(`${start}_to_${end}_analytics_domain_data.csv`, csv);
};

/* ---------------- ONE BUTTON: DOWNLOAD BOTH ---------------- */

export const downloadRevenueAndAnalyticsCSVs = ({
	partnerName,
	filters,
	revenueDomainRows = [],
	analyticsDomainRows = []
}) => {
	downloadRevenueDomainCSV({
		partnerName,
		filters,
		revenueDomainRows,
		analyticsDomainRows,
		decimalPlaces: 2
	});

	downloadAnalyticsDomainCSV({
		partnerName,
		filters,
		analyticsDomainRows,
		decimalPlaces: 2
	});
};
